import { inject, Injectable } from '@angular/core';
import { format } from 'date-fns';
import { ConfigBaseFilter, ConfigService, ListLayout, ListLayoutMultipleComponent, ListSortOption, SimpleConfigBaseFilter } from 'gung-list';
import { first, map, Observable, switchMap } from 'rxjs';
import { InvoiceTableComponent } from '../../components/invoice-table/invoice-table.component';
import { Invoice } from '../../models/invoice';
import { InvoiceService } from '../invoices/invoice.service';
import { SelectedCustomerService } from '../selected-customer/selected-customer.service';

@Injectable({
  providedIn: 'root'
})
export class InvoiceListConfigService implements ConfigService<Invoice> {
  protected invoiceService = inject(InvoiceService);
  protected selectedCustomerService = inject(SelectedCustomerService);

  /*
   This variable and setter is called in the invoice-list-component to set
   the customerId to the id in the route params. It did not work to get the routeparams
   from this service.
   */
  customerId: string;
  topFilter = true;
  public hasStatus = false;
  setCustomerId(customerId: string) {
    this.customerId = customerId;
  }

  getItems(): Observable<Invoice[]> {
    if (this.customerId == null) {
      return this.selectedCustomerService.getSelectedCustomer().pipe(
        first(),
        switchMap(cust => this.getCustomerInvoices(cust.id))
      );
    }
    return this.getCustomerInvoices(this.customerId);
  }

  getCustomerInvoices(customerId: string): Observable<Invoice[]> {
    return this.invoiceService.getInvoices(customerId).pipe(
      map(invoices => {
        this.hasStatus = invoices.some(invoice => invoice.extra?.gme?.status);
        return invoices;
      })
    );
  }

  getFilters(): ConfigBaseFilter<Invoice>[] {
    return [new InvoicesStatusFilter(), new InvoiceDateFilter(), new InvoiceDueDateFilter()];
  }

  getSortOptions(): ListSortOption<Invoice>[] {
    // TODO: implement
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<Invoice>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => InvoiceTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'Invoice grid'
      }
    ];
  }

  getSearchTerms(item: Invoice): string[] {
    return [item.id];
  }

  getItemId(item: Invoice): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}

class InvoicesStatusFilter extends SimpleConfigBaseFilter<Invoice> {
  getName(): string {
    return 'INVOICE_STATUS';
  }

  getOptionIds(item: Invoice): string[] {
    return [item.extra?.gme?.status];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class InvoiceDateFilter extends SimpleConfigBaseFilter<Invoice> {
  type = 'dateRangeFilter';

  getName(): string {
    return 'INVOICE_DATE';
  }

  getOptionIds(item: Invoice): string[] {
    if (item?.extra?.gme?.invoiceDate) {
      return [format(new Date(item?.extra?.gme?.invoiceDate), 'yyMMdd')];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class InvoiceDueDateFilter extends SimpleConfigBaseFilter<Invoice> {
  type = 'dateRangeFilter';

  getName(): string {
    return 'DUE_DATE';
  }

  getOptionIds(item: Invoice): string[] {
    if (item?.extra?.gme?.dueDate) {
      return [format(new Date(item?.extra?.gme?.dueDate), 'yyMMdd')];
    }
    return [];
  }

  getOptionName(key: string): string {
    return key;
  }
}
